/**
 * Faye travel insurance at checkout: what to offer, what the browser sends,
 * how the server re-prices it, and what gets written once the booking exists.
 *
 * Plain JS on purpose, like faye-pricing.js and stay-cancel-rules.js: Review
 * builds the offer with these functions, both Defera Capital checkout
 * functions (Deno) verify the selection with them, and the booking writers
 * (Deno and the Express server) persist it with them, so the browser and the
 * server can never price a plan differently.
 *
 * The money model lives in trip-insurance.js: the premium is added to the
 * plan total AFTER Defera's fees, never to the deposit, and recorded on the
 * booking (`insurance_premium_cents`) and in `trip_insurance_policies`.
 */
import {
  FAYE_RULES,
  fayeOfferEligibility,
  fayePlan,
  fayeTripRegion,
  quoteFaye,
} from './faye-pricing.js';
import { readCheckoutMiles } from './miles-redemption.js';

/** Plans checkout offers. Flagship stays off: its price moved with trip cost
 *  and state in ways only one quote each pinned down. */
export const OFFERED_TIERS = Object.freeze(['first', 'forward']);

/** Bump when the disclosures the customer scrolls through change. Stored with
 *  every policy as proof of what was accepted. */
export const FAYE_DISCLOSURE_VERSION = 'faye-2026-10-02';

const DAY = /^(\d{4})-(\d{2})-(\d{2})/;
const isoDay = (v) => {
  const m = DAY.exec(String(v ?? ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** Completed years between a YYYY-MM-DD birthdate and `onDate`; null if unknown. */
export function ageOn(bornOn, onDate) {
  const b = DAY.exec(String(bornOn ?? ''));
  const d = DAY.exec(String(onDate ?? ''));
  if (!b || !d) return null;
  let age = Number(d[1]) - Number(b[1]);
  if (Number(d[2]) < Number(b[2]) || (Number(d[2]) === Number(b[2]) && Number(d[3]) < Number(b[3]))) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/** A traveler's birthdate, whatever shape the passenger arrived in. */
export function travelerBornOn(p) {
  return isoDay(p?.born_on ?? p?.bornOn ?? p?.date_of_birth ?? p?.dateOfBirth);
}

/** A traveler's display name for the policy record. */
export function travelerName(p) {
  const first = String(p?.given_name ?? p?.first_name ?? p?.firstName ?? p?.givenName ?? '').trim();
  const last = String(p?.family_name ?? p?.last_name ?? p?.lastName ?? p?.familyName ?? '').trim();
  return [first, last].filter(Boolean).join(' ') || null;
}

/**
 * What Faye prices a trip on, read from the offer's slices: departure day,
 * the day the traveler gets home (null on a one-way), every airport, and
 * whether it leaves the US.
 */
export function fayeTripFacts(flightData, timezoneForAirport) {
  const slices = Array.isArray(flightData?.slices) ? flightData.slices : [];
  const segs = (sl) => (Array.isArray(sl?.segments) ? sl.segments : []);
  const first = segs(slices[0])[0];
  const lastSlice = slices.length > 1 ? slices[slices.length - 1] : null;
  const lastSeg = lastSlice ? segs(lastSlice)[segs(lastSlice).length - 1] : null;
  const airports = slices.flatMap((sl) => segs(sl).flatMap((s) => [s?.origin?.iata_code, s?.destination?.iata_code]));
  return {
    departDate: isoDay(first?.departing_at),
    returnDate: lastSeg ? isoDay(lastSeg.arriving_at || lastSeg.departing_at) : null,
    airports: airports.filter(Boolean),
    region: fayeTripRegion(airports, timezoneForAirport),
  };
}

const toCents = (dollars) => Math.round((Number(dollars) || 0) * 100);

/**
 * The checkout offer: whether this trip can be offered Faye, and the price of
 * each plan. Null when the feature is off or the rail isn't Defera Capital.
 *
 * @param {object} p
 * @param {boolean} p.enabled           the launch switch
 * @param {string}  p.paymentMethod     the checkout's effective rail
 * @param {object}  p.flightData        the selected offer (slices…)
 * @param {object[]} p.travelers        passengers with a birthdate
 * @param {number}  p.baseFare          airfare, all travelers, before credits (dollars)
 * @param {number}  p.tripCost          the travel total the customer pays, before insurance (dollars)
 * @param {number}  p.paymentsAfterDeposit
 * @param {string}  [p.residenceState]
 * @param {string}  p.today             YYYY-MM-DD
 * @param {Function} p.timezoneForAirport
 */
export function buildFayeOffer({
  enabled, paymentMethod, flightData, travelers, baseFare, tripCost, paymentsAfterDeposit,
  residenceState = null, today, timezoneForAirport,
}) {
  if (!enabled || paymentMethod !== 'defera_capital') return null;
  const facts = fayeTripFacts(flightData, timezoneForAirport);
  const ages = (Array.isArray(travelers) ? travelers : []).map((p) => ageOn(travelerBornOn(p), today));
  const eligibility = fayeOfferEligibility({
    baseFare,
    ages,
    tripCost,
    departDate: facts.departDate,
    returnDate: facts.returnDate,
    region: facts.region,
    remainingPayments: paymentsAfterDeposit,
    today,
  });
  if (!eligibility.eligible) return { eligible: false, reason: eligibility.reason, detail: eligibility.detail, facts, plans: [] };
  const international = facts.region === 'international';
  const plans = OFFERED_TIERS.map((tier) => {
    const q = quoteFaye({
      tier, ages, tripCost, departDate: facts.departDate, returnDate: facts.returnDate,
      state: residenceState || 'CA', international, purchaseDate: today,
    });
    if (!q.ok) return null;
    const plan = fayePlan(tier, { international });
    return {
      tier,
      label: plan.label,
      international,
      highlights: plan.highlights,
      compare: plan.compare,
      coverage: plan.coverage,
      excludedReasons: plan.excludedReasons,
      premiumCents: toCents(q.premium),
      exact: q.exact,
      notes: q.notes,
      warnings: q.warnings,
    };
  }).filter(Boolean);
  return plans.length
    ? { eligible: true, reason: null, detail: null, facts, ages, plans }
    : { eligible: false, reason: 'quote', detail: 'no plan could be priced', facts, plans: [] };
}

/** What the browser sends with the checkout when the customer added a plan. */
export function buildInsuranceSelection({ plan, acceptedAt }) {
  if (!plan) return null;
  return {
    tier: plan.tier,
    premium_cents: plan.premiumCents,
    disclosure_version: FAYE_DISCLOSURE_VERSION,
    disclosures_accepted_at: acceptedAt,
  };
}

/**
 * @typedef {{ ok: false, status: number, code: string, error: string }} InsuranceRefusal
 * @typedef {{ ok: true, record: Record<string, any> | null }} InsuranceAccepted
 */

/** @returns {InsuranceRefusal} */
const fail = (status, code, error) => ({ ok: false, status, code, error });

/**
 * Server side: re-price a selection from the checkout's own inputs and say
 * whether to accept it. The ages come from the passengers on the checkout,
 * never from the selection, and the trip cost is the plan total less the
 * premium. Returns the record kept with the pending checkout and copied
 * onto the policy.
 *
 * @param {object} p
 * @param {*}           p.selection           body.insurance (null when none)
 * @param {boolean}     p.enabled             the launch switch
 * @param {string}      p.paymentMethod       'defera_capital' for both Capital rails
 * @param {*}           p.flightData
 * @param {*}           p.passengers          body.passengers
 * @param {number}      p.baseFareCents       body.base_fare_cents (airfare, all travelers)
 * @param {number}      p.totalAmountCents    body.total_amount_cents (premium included)
 * @param {number}      p.depositCents
 * @param {number}      p.installmentCount
 * @param {number|null} [p.installmentAmountCents]  checked only when the client sets the rows (NMI)
 * @param {string|null} [p.residenceState]
 * @param {string}      p.today               YYYY-MM-DD
 * @param {(code: string) => string|null} p.timezoneForAirport
 * @returns {InsuranceAccepted | InsuranceRefusal}
 */
export function verifyInsuranceSelection({
  selection, enabled, paymentMethod, flightData, passengers, baseFareCents, totalAmountCents,
  depositCents, installmentCount, installmentAmountCents = null, residenceState = null, today, timezoneForAirport,
}) {
  if (!selection) return /** @type {InsuranceAccepted} */ ({ ok: true, record: null });
  if (!enabled) return fail(400, 'insurance_unavailable', 'Travel insurance is not available yet.');
  if (paymentMethod !== 'defera_capital') return fail(400, 'insurance_unavailable', 'Travel insurance is only available on Defera Installments.');
  // Terms 2.17: not available on bookings that use Defera Miles.
  if (readCheckoutMiles(flightData)) return fail(400, 'insurance_unavailable', 'Travel insurance can’t be combined with Defera Miles.');
  const tier = String(selection.tier || '');
  if (!OFFERED_TIERS.includes(tier)) return fail(400, 'insurance_plan', 'Choose Faye First or Faye Forward.');
  if (selection.disclosure_version !== FAYE_DISCLOSURE_VERSION || !selection.disclosures_accepted_at) {
    return fail(400, 'insurance_disclosures', 'Please read and accept the travel insurance disclosures again.');
  }
  const premiumCents = Math.round(Number(selection.premium_cents));
  const total = Math.round(Number(totalAmountCents));
  if (!(premiumCents > 0) || !(total > premiumCents)) return fail(400, 'insurance_price', 'The travel insurance price is missing.');

  const list = Array.isArray(passengers) ? passengers : [];
  const ages = list.map((p) => ageOn(travelerBornOn(p), today));
  const facts = fayeTripFacts(flightData, timezoneForAirport);
  const tripCost = (total - premiumCents) / 100;
  const baseFare = Number(baseFareCents) > 0 ? Number(baseFareCents) / 100 : Number(flightData?.total_amount);
  const eligibility = fayeOfferEligibility({
    baseFare, ages, tripCost, departDate: facts.departDate, returnDate: facts.returnDate,
    region: facts.region, remainingPayments: installmentCount, today,
  });
  if (!eligibility.eligible) return fail(400, `insurance_${eligibility.reason}`, `Travel insurance isn't available on this trip (${eligibility.detail}).`);

  const international = facts.region === 'international';
  const quote = quoteFaye({
    tier, ages, tripCost, departDate: facts.departDate, returnDate: facts.returnDate,
    state: residenceState || 'CA', international, purchaseDate: today,
  });
  if (!quote.ok) return fail(400, 'insurance_quote', quote.error);
  const serverCents = toCents(quote.premium);
  // Faye rounds each traveler to the cent, so a trip cost a cent apart (the
  // browser's float total at a half-cent boundary) can move every traveler by
  // a cent. Within that, the price is the same price.
  if (Math.abs(serverCents - premiumCents) > Math.max(1, ages.length)) {
    return fail(409, 'insurance_price_changed', 'The travel insurance price changed. Please review it and try again.');
  }
  // Rates are filed by state, and an unknown home state is priced as
  // California: say so, so ops checks the portal price before buying.
  const stateKnown = Boolean(residenceState && String(residenceState).trim());
  // When the browser sets the installment rows (NMI), they must carry the
  // premium too: deposit + rows has to cover the whole plan total.
  if (installmentAmountCents != null) {
    const n = Math.max(0, Math.floor(Number(installmentCount) || 0));
    const covered = Math.round(Number(depositCents) || 0) + n * Math.round(Number(installmentAmountCents) || 0);
    if (Math.abs(covered - total) > Math.max(1, n)) {
      return fail(400, 'insurance_schedule', "The payment plan doesn't include the travel insurance. Please try again.");
    }
  }

  const plan = fayePlan(tier, { international });
  return /** @type {InsuranceAccepted} */ ({
    ok: true,
    record: {
      v: 1,
      provider: 'faye',
      tier,
      label: plan.label,
      international,
      // The premium inside the total the customer agreed to (within a cent a
      // traveler of the server's own quote, which the quote record keeps).
      premium_cents: premiumCents,
      insured_cost_cents: Math.round(Math.max(quote.insuredCost, 0) * 100),
      base_fare_cents: Math.round(baseFare * 100),
      travelers: list.map((p, i) => ({ name: travelerName(p), age: ages[i] })),
      depart_date: facts.departDate,
      return_date: facts.returnDate,
      residence_state: residenceState ? String(residenceState).toUpperCase().slice(0, 2) : null,
      quote: {
        inputs: { tier, ages, trip_cost: tripCost, depart_date: facts.departDate, return_date: facts.returnDate, international, state: residenceState || 'CA', priced_on: today },
        result: {
          premium: quote.premium,
          breakdown: quote.breakdown,
          exact: quote.exact && stateKnown,
          notes: stateKnown ? quote.notes : [...(quote.notes || []), 'home state unknown: priced as a California resident'],
          warnings: quote.warnings,
        },
      },
      coverage: { highlights: plan.highlights, coverage: plan.coverage, excludedReasons: plan.excludedReasons, compare: plan.compare },
      disclosure_version: selection.disclosure_version,
      disclosures_accepted_at: String(selection.disclosures_accepted_at),
      free_look_days: FAYE_RULES.freeLookDays,
    },
  });
}

/** The `trip_insurance_policies` row for a checkout record. */
export function policyRowFromRecord({ record, bookingId, userId, tripReference, residenceState = null }) {
  return {
    booking_id: bookingId,
    user_id: userId,
    trip_reference: tripReference || null,
    provider: 'faye',
    plan: record.tier,
    plan_label: record.label,
    international: record.international === true,
    premium_cents: record.premium_cents,
    insured_cost_cents: record.insured_cost_cents,
    quote: record.quote,
    coverage: record.coverage,
    travelers: record.travelers,
    depart_date: record.depart_date,
    return_date: record.return_date,
    residence_state: record.residence_state || (residenceState ? String(residenceState).toUpperCase().slice(0, 2) : null),
    disclosure_version: record.disclosure_version,
    disclosures_accepted_at: record.disclosures_accepted_at,
    status: 'pending',
  };
}

/**
 * Once the booking row exists: record the premium on it (when the writer
 * didn't already) and create its policy. Idempotent — both booking writers
 * (the checkout save and its webhook backup) may call it. Works with the
 * supabase-js client in Node and Deno alike.
 *
 * @returns {Promise<{ ok: boolean, action: string, error?: string }>}
 */
export async function persistInsuranceForBooking(supabase, { record, bookingId, userId, tripReference, residenceState = null }) {
  if (!record || !(record.premium_cents > 0) || !bookingId) return { ok: true, action: 'none' };
  const { error: premiumErr } = await supabase
    .from('bookings')
    .update({ insurance_premium_cents: record.premium_cents })
    .eq('id', bookingId)
    .is('insurance_premium_cents', null);
  if (premiumErr) return { ok: false, action: 'premium', error: premiumErr.message };
  const { error } = await supabase
    .from('trip_insurance_policies')
    .insert(policyRowFromRecord({ record, bookingId, userId, tripReference, residenceState }));
  if (error) {
    const dupe = error.code === '23505' || /duplicate|unique/i.test(error.message || '');
    return dupe ? { ok: true, action: 'exists' } : { ok: false, action: 'policy', error: error.message };
  }
  return { ok: true, action: 'created' };
}
